import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppActions } from "../../../../store/ducks/app";
import { AuthActions } from "../../../../store/ducks/auth";
import { DashBoardActions } from "../../../../store/ducks/dashboard";
import { TextsProps } from "../../../../database/txt.interface";
import { handlePaciente } from "../../../../utils/funcs";
import {
	CopilotPatientReference,
	fetchFullPatientForDashboard,
	fetchOperationalPatients,
	isAbortError,
} from "../../services/clinicalDashboardService";

const PAGE_SIZE = 8;

export interface PatientSelection {
	id: number;
	name: string;
	initials: string;
	photoUrl: string | null;
	original: CopilotPatientReference;
}

const mapPatient = (patient: CopilotPatientReference): PatientSelection => {
	const name = patient.nome || "";
	const names = name.trim().split(/\s+/).filter(Boolean);
	return {
		id: patient.id,
		name,
		initials:
			names.length > 1
				? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
				: name.charAt(0).toUpperCase(),
		photoUrl: patient.foto || null,
		original: patient,
	};
};

export const usePatientListSelectionModalViewModel = (
	isOpen: boolean,
	onClose: () => void
) => {
	const dispatch = useDispatch();
	const token = useSelector((state: any) => state?.auth?.user?.token) || "";
	const configServices = useSelector(
		(state: any) => state.configs.config_servicos
	);
	const currentLanguage: TextsProps = useSelector(
		(state: any) => state.configs.current_language
	);
	const [searchQuery, setSearchQuery] = useState("");
	const [patients, setPatients] = useState<PatientSelection[]>([]);
	const [page, setPage] = useState(1);
	const [hasExtraPatients, setHasExtraPatients] = useState(false);
	const [patientsLoading, setPatientsLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [startingPatientId, setStartingPatientId] = useState<number | null>(
		null
	);
	const listRequest = useRef<AbortController | null>(null);
	const startRequest = useRef<AbortController | null>(null);
	const requestSequence = useRef(0);

	const load = useCallback(
		async (nextPage: number, append: boolean, query: string) => {
			if (!token) {
				setError("Sua sessão não está disponível. Entre novamente.");
				return;
			}

			listRequest.current?.abort();
			const controller = new AbortController();
			listRequest.current = controller;
			const sequence = ++requestSequence.current;
			setError(null);
			setPatientsLoading(true);
			setLoadingMore(append);

			try {
				const result = await fetchOperationalPatients({
					token,
					search: query,
					page: nextPage,
					limit: PAGE_SIZE,
					signal: controller.signal,
				});
				if (sequence !== requestSequence.current) return;
				const incoming = result.items.map(mapPatient);
				setPatients((current) => {
					if (!append) return incoming;
					return Array.from(
						new Map(
							[...current, ...incoming].map((patient) => [patient.id, patient])
						).values()
					);
				});
				setPage(result.paginacao?.pagina || nextPage);
				setHasExtraPatients(!!result.paginacao?.temProxima);
			} catch (requestError) {
				if (
					!isAbortError(requestError) &&
					sequence === requestSequence.current
				) {
					if ((requestError as any)?.status === 401) {
						dispatch(AuthActions.auth_logout());
					}
					setError(
						(requestError as Error)?.message ||
							"Não foi possível carregar os pacientes."
					);
				}
			} finally {
				if (sequence === requestSequence.current) {
					setPatientsLoading(false);
					setLoadingMore(false);
				}
			}
		},
		[token, dispatch]
	);

	useEffect(() => {
		if (!isOpen) {
			requestSequence.current += 1;
			listRequest.current?.abort();
			startRequest.current?.abort();
			setStartingPatientId(null);
			return;
		}
		setSearchQuery("");
		setPatients([]);
		setPage(1);
		setHasExtraPatients(false);
		setError(null);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const timeout = setTimeout(() => load(1, false, searchQuery), 300);
		return () => {
			clearTimeout(timeout);
			requestSequence.current += 1;
			listRequest.current?.abort();
		};
	}, [searchQuery, isOpen, load]);

	useEffect(
		() => () => {
			requestSequence.current += 1;
			listRequest.current?.abort();
			startRequest.current?.abort();
		},
		[]
	);

	const handleStartEvaluation = async (patient: PatientSelection) => {
		if (!token || startingPatientId !== null) return;
		startRequest.current?.abort();
		const controller = new AbortController();
		startRequest.current = controller;
		setStartingPatientId(patient.id);
		setError(null);

		try {
			const fullPatient = await fetchFullPatientForDashboard(
				token,
				patient.id,
				controller.signal
			);
			if (!fullPatient) {
				throw new Error("Não foi possível carregar o cadastro do paciente.");
			}
			handlePaciente(fullPatient, dispatch, configServices);
			dispatch(DashBoardActions.protocolos_request());
			dispatch(DashBoardActions.set_current_page("protocols_in_patient"));
			dispatch(
				AppActions.set_option_menu(false, currentLanguage?.menu_lateral_inicio)
			);
			onClose();
		} catch (requestError) {
			if (!isAbortError(requestError)) {
				if ((requestError as any)?.status === 401) {
					dispatch(AuthActions.auth_logout());
				}
				setError(
					(requestError as Error)?.message ||
						"Não foi possível iniciar a avaliação."
				);
			}
		} finally {
			if (!controller.signal.aborted) setStartingPatientId(null);
		}
	};

	return {
		searchQuery,
		filteredPatients: patients,
		hasExtraPatients,
		patientsLoading,
		loadingMore,
		error,
		startingPatientId,
		handleSearchChange: setSearchQuery,
		handleStartEvaluation,
		handleLoadMore: () => load(page + 1, true, searchQuery),
		retry: () => load(1, false, searchQuery),
	};
};
